import useRequest from "@/lib/request/useRequest";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import {
    faPlus,
    faSearch,
    faPenToSquare,
    faFilter,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Autocomplete,
    AutocompleteItem,
    Button,
    Chip,
    Divider,
    Input,
    Select,
    SelectItem,
} from "@heroui/react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import TableV2 from "@/components/table/TableV2";
import useIsMobile from "@/lib/utils/useIsMobile";
import MobileNetworkFilterDrawer from "@/components/network/MobileNetworkFilter";
import MobileUserCard from "@/components/network/MobileUserCard";
import {
    formatAccountType,
    getOrgName,
    sortUsers,
} from "@/components/network/networkUtils";
import useAuth from "@/lib/auth/useAuth";
import { Organization } from "@/types/organization";
import { User } from "@/types/user";

type NetworkUsersSearch = {
    search?: string;
    ordering?: string;
    organization?: string;
    account_type?: string;
    status?: string;
};

type NetworkUser = {
    id: number;
    username: string;
    full_name: string;
    is_staff: boolean;
    is_active: boolean;
    account_type: string;
    organization: string;
    service_provider_id: number;
    is_group_admin: boolean;
    hubspot_link: null;
    organization_id: number;
};

export const Route = createFileRoute("/dashboard/network/users")({
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown>): NetworkUsersSearch => {
        return {
            search: search.search as string,
            ordering: search.ordering as string,
            organization: search.organization as string,
            account_type: search.account_type as string,
            status: search.status as string,
        };
    },
});

const USER_SORT_OPTIONS = [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "organization", label: "Organization" },
    { key: "account_type", label: "Type" },
    { key: "status", label: "Status" },
];

function RouteComponent() {
    const { useGetRequest } = useRequest();

    const { search, ordering, organization, account_type, status } =
        Route.useSearch();

    const navigate = Route.useNavigate();
    const { isMobile } = useIsMobile();
    const { user, userIsSentinel, isConsultant } = useAuth();
    const canInviteUser =
        userIsSentinel || isConsultant || user?.is_group_admin;

    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [debouncedSearch, setDebouncedSearch] = useState(search ?? "");

    useEffect(() => {
        setDebouncedSearch(search ?? "");
    }, [search]);

    useEffect(() => {
        if (debouncedSearch === search) return;
        const handler = setTimeout(() => {
            navigate({
                search: (prev) => ({
                    ...prev,
                    search: debouncedSearch,
                }),
            });
        }, 700);

        return () => clearTimeout(handler);
    }, [debouncedSearch, navigate, search]);

    const usersQuery = useGetRequest<NetworkUser[]>({
        url: "users/",
    });

    const organizationOptions = useMemo(() => {
        const names = (usersQuery.data ?? [])
            .map((u) => getOrgName(u.organization))
            .filter((name): name is string => !!name);

        return Array.from(new Set(names)).sort();
    }, [usersQuery.data]);

    const tableData = useMemo(() => {
        let data = usersQuery.data ?? [];

        if (search?.trim()) {
            const q = search.trim().toLowerCase();
            data = data.filter(
                (u) =>
                    u.full_name?.toLowerCase().includes(q) ||
                    u.username?.toLowerCase().includes(q) ||
                    getOrgName(u.organization).toLowerCase().includes(q),
            );
        }

        if (organization && organization !== "all") {
            data = data.filter(
                (u) => getOrgName(u.organization) === organization,
            );
        }

        if (account_type && account_type !== "all") {
            data = data.filter((u) => u.account_type === account_type);
        }

        if (status === "active" || !status) {
            data = data.filter((u) => u.is_active);
        }

        if (status === "inactive") {
            data = data.filter((u) => !u.is_active);
        }

        return data;
    }, [usersQuery.data, search, organization, account_type, status]);

    const [sorting, setSorting] = useState<SortingState>([
        {
            id: ordering
                ? ordering.startsWith("-")
                    ? ordering.slice(1)
                    : ordering
                : "name",
            desc: ordering ? ordering.startsWith("-") : false,
        },
    ]);

    useEffect(() => {
        if (sorting.length > 0) {
            navigate({
                search: (prev) => ({
                    ...prev,
                    ordering: `${sorting[0].desc ? "-" : ""}${sorting[0].id}`,
                }),
            });
        } else {
            navigate({
                search: (prev) => ({ ...prev, ordering: "" }),
            });
        }
    }, [sorting, navigate]);

    const mobileData = useMemo(() => {
        return sortUsers(tableData as unknown as User[], sorting);
    }, [tableData, sorting]);

    const organizationFilterOptions = useMemo(() => {
        return organizationOptions.map((name) => ({
            key: name,
            label: name,
        }));
    }, [organizationOptions]);

    const mobileFilters = useMemo(
        () => [
            {
                id: "organization",
                label: "Organization",
                placeholder: "Organization",
                inputType: "autocomplete" as const,
                value: organization,
                options: organizationFilterOptions,
            },
            {
                id: "account_type",
                label: "Account Type",
                inputType: "select" as const,
                value: account_type,
                allLabel: "All Types",
                options: [
                    { key: "grower", label: "Grower" },
                    { key: "consultant", label: "Consultant" },
                    { key: "sentinel", label: "Sentinel" },
                ],
            },
            {
                id: "status",
                label: "Status",
                inputType: "select" as const,
                value: status ?? "active",
                defaultValue: "active",
                allLabel: "All Statuses",
                options: [
                    { key: "active", label: "Active" },
                    { key: "inactive", label: "Inactive" },
                ],
            },
        ],
        [account_type, organization, organizationFilterOptions, status],
    );

    const activeMobileFilterCount = useMemo(() => {
        return [
            search?.trim(),
            organization && organization !== "all",
            account_type && account_type !== "all",
            status && status !== "active",
        ].filter(Boolean).length;
    }, [account_type, organization, search, status]);

    const mobileIsLoading = usersQuery.isLoading && mobileData.length === 0;

    const columns: ColumnDef<NetworkUser>[] = useMemo(
        () => [
            {
                id: "name",
                header: "Name",
                accessorFn: (row) => row.full_name,
                cell: (info) => (
                    <div className="text-sm font-medium px-2">
                        {info.getValue() as string}
                    </div>
                ),
                size: 200,
            },
            {
                id: "email",
                header: "Email",
                accessorFn: (row) => row.username,
                cell: (info) => (
                    <div className="text-sm px-2">
                        {info.getValue() as string}
                    </div>
                ),
                size: 300,
            },
            {
                id: "organization",
                header: "Organization",
                accessorFn: (row) => getOrgName(row.organization),
                cell: (info) => (
                    <div className="text-sm px-2">
                        {info.getValue() as string}
                    </div>
                ),
                size: 200,
            },
            {
                id: "account_type",
                header: "Type",
                accessorFn: (row) => row.account_type,
                cell: ({ row }) => {
                    const type = row.original.account_type;
                    const color =
                        type === "sentinel"
                            ? "primary"
                            : type === "consultant"
                              ? "secondary"
                              : "default";
                    return (
                        <Chip
                            size="sm"
                            radius="sm"
                            variant="flat"
                            color={color}
                        >
                            {formatAccountType(type)}
                        </Chip>
                    );
                },
                size: 120,
            },
            {
                id: "status",
                header: "Status",
                accessorFn: (row) => row.is_active,
                cell: ({ row }) => (
                    <Chip
                        size="sm"
                        radius="sm"
                        variant="flat"
                        color={row.original.is_active ? "success" : "danger"}
                    >
                        {row.original.is_active ? "Active" : "Inactive"}
                    </Chip>
                ),
                size: 100,
            },
            {
                id: "edit",
                enableSorting: false,
                header: "",
                accessorFn: (row) => row.id,
                cell: ({ row }) => {
                    const orgId = (user?.organization as Organization)?.id;

                    if (
                        !userIsSentinel &&
                        !isConsultant &&
                        orgId != row.original.organization_id
                    ) {
                        return null;
                    }

                    return (
                        <div onClick={(e) => e.stopPropagation()}>
                            <Button
                                variant="flat"
                                size="sm"
                                startContent={
                                    <FontAwesomeIcon icon={faPenToSquare} />
                                }
                                onPress={() => {
                                    navigate({
                                        to: "/dashboard/network/users/$userId",
                                        params: {
                                            userId: row.original.id.toString(),
                                        },
                                        search: (prev) => prev,
                                    });
                                }}
                            >
                                Edit
                            </Button>
                        </div>
                    );
                },
                size: 90,
            },
        ],
        [navigate, user?.organization, isConsultant, userIsSentinel],
    );

    return (
        <div className="h-full flex flex-col overflow-hidden px-0 md:px-4">
            {isMobile ? (
                <div className="flex flex-row gap-2 p-2 items-center justify-between w-full">
                    <Button
                        size="sm"
                        color={
                            activeMobileFilterCount > 0 ? "primary" : "default"
                        }
                        onPress={() => setMobileFiltersOpen(true)}
                        startContent={<FontAwesomeIcon icon={faFilter} />}
                    >
                        Filters
                        {activeMobileFilterCount > 0
                            ? ` (${activeMobileFilterCount})`
                            : ""}
                    </Button>

                    {canInviteUser && (
                        <Button
                            as={Link}
                            to="/dashboard/network/users/new"
                            color="primary"
                            size="sm"
                            startContent={<FontAwesomeIcon icon={faPlus} />}
                        >
                            Add User
                        </Button>
                    )}
                </div>
            ) : (
                <div className="flex flex-row gap-2 p-2 items-center justify-between w-full">
                    <div className="flex flex-col lg:flex-row gap-2 items-center">
                        <Input
                            size="sm"
                            placeholder="Search..."
                            value={debouncedSearch}
                            onValueChange={setDebouncedSearch}
                            startContent={<FontAwesomeIcon icon={faSearch} />}
                            className="w-64"
                        />

                        <Autocomplete
                            size="sm"
                            aria-label="Organization"
                            placeholder="Organization"
                            selectedKey={
                                organization && organization !== "all"
                                    ? organization
                                    : null
                            }
                            onSelectionChange={(key) => {
                                navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        organization:
                                            key == null
                                                ? undefined
                                                : String(key),
                                    }),
                                });
                            }}
                            className="w-56"
                        >
                            {organizationOptions.map((name) => (
                                <AutocompleteItem key={name} textValue={name}>
                                    {name}
                                </AutocompleteItem>
                            ))}
                        </Autocomplete>

                        <Select
                            size="sm"
                            aria-label="Account Type"
                            selectedKeys={[account_type ?? "all"]}
                            onChange={(e) =>
                                navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        account_type:
                                            e.target.value === "all"
                                                ? undefined
                                                : e.target.value,
                                    }),
                                })
                            }
                            className="w-40"
                        >
                            <SelectItem key="all">All Types</SelectItem>
                            <SelectItem key="grower">Grower</SelectItem>
                            <SelectItem key="consultant">Consultant</SelectItem>
                            <SelectItem key="sentinel">Sentinel</SelectItem>
                        </Select>

                        <Select
                            size="sm"
                            aria-label="Status"
                            selectedKeys={[status ?? "active"]}
                            onChange={(e) =>
                                navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        status:
                                            e.target.value === "active"
                                                ? undefined
                                                : e.target.value,
                                    }),
                                })
                            }
                            className="w-40"
                        >
                            <SelectItem key="all">All Statuses</SelectItem>
                            <SelectItem key="active">Active</SelectItem>
                            <SelectItem key="inactive">Inactive</SelectItem>
                        </Select>
                    </div>

                    <div className="flex flex-row gap-2 items-center">
                        {canInviteUser && (
                            <Button
                                as={Link}
                                to="/dashboard/network/users/new"
                                color="primary"
                                size="sm"
                                startContent={
                                    <FontAwesomeIcon icon={faPlus} />
                                }
                            >
                                Add User
                            </Button>
                        )}
                    </div>
                </div>
            )}

            <Divider />

            {isMobile ? (
                <div className="flex-1 min-h-0 overflow-y-auto">
                    {mobileIsLoading ? (
                        <div className="p-4 text-sm text-gray-500">
                            Loading users...
                        </div>
                    ) : mobileData.length === 0 ? (
                        <div className="p-4 text-sm text-gray-500">
                            No users found.
                        </div>
                    ) : (
                        mobileData.map((user) => (
                            <div key={user.id} className="w-full p-1 pb-0">
                                <MobileUserCard
                                    user={user}
                                    onPress={() =>
                                        navigate({
                                            to: "/dashboard/network/users/$userId",
                                            params: {
                                                userId: user.id.toString(),
                                            },
                                            search: (prev) => prev,
                                        })
                                    }
                                />
                            </div>
                        ))
                    )}
                </div>
            ) : (
                <TableV2
                    data={tableData}
                    columns={columns}
                    sorting={sorting}
                    setSorting={setSorting}
                    isLoading={usersQuery.isLoading}
                    showScrollbar={true}
                />
            )}

            <Outlet />

            <MobileNetworkFilterDrawer
                isOpen={mobileFiltersOpen}
                onClose={() => setMobileFiltersOpen(false)}
                searchValue={search}
                filters={mobileFilters}
                ordering={ordering}
                sortOptions={USER_SORT_OPTIONS}
                onApply={({ searchValue, filters, sortKey, sortDesc }) => {
                    if (sortKey) {
                        setSorting([{ id: sortKey, desc: sortDesc }]);
                    }

                    navigate({
                        search: (prev) => ({
                            ...prev,
                            search: searchValue,
                            organization: filters.organization,
                            account_type:
                                filters.account_type === "all"
                                    ? undefined
                                    : filters.account_type,
                            status:
                                filters.status === "active"
                                    ? undefined
                                    : filters.status,
                        }),
                    });
                }}
                onClear={() => {
                    navigate({
                        search: (prev) => ({
                            ...prev,
                            search: "",
                            organization: undefined,
                            account_type: undefined,
                            status: undefined,
                        }),
                    });
                }}
            />
        </div>
    );
}
